import SealdSDK, { SealdSDK as SealdSDKInstance } from "@seald-io/sdk";
import SealdSDKPluginSSKS2MR from "@seald-io/sdk-plugin-ssks-2mr";
import { create } from "zustand";
import { combine } from "zustand/middleware";
import { Sentry } from "@shared/utils/init-tracking";

import { useApi } from "@shared/features/api/hooks/useApi";
import { config } from "@shared/config";

let sealdSDKInstance: SealdSDKInstance = null;
let sealdDatabasePath: string | null = null;
const useSealdStore = create(
  combine(
    {
      challengeData: null,
      sldId: null,
      groups: [],
    },
    (set) => ({
      setChallengeData: (data) => set({ challengeData: data }),
      setSldId: (sldId) => set({ sldId }),
      setGroups: (groups) => set({ groups }),
      addGroup: (group) =>
        set((state) => ({
          groups: [...state.groups, group],
        })),
    })
  )
);

export const useSeald = () => {
  const api = useApi();
  const APP_ID = config.VITE_SEALD_APP_ID;
  const API_URL = config.VITE_SEALD_API_URL;
  const STORAGE_URL = config.VITE_SEALD_STORAGE_URL;
  const store = useSealdStore();

  const userIdToConnector = (userId: string) => {
    return {
      value: `${userId}@${APP_ID}`,
      type: "AP",
    };
  };

  const instantiateSealdSDK = async ({
    databaseKey,
    sessionID,
  }: {
    databaseKey?: string;
    sessionID?: string;
  } = {}) => {
    if (
      sealdSDKInstance &&
      sealdDatabasePath === `seald-session-${sessionID}`
    ) {
      return sealdDatabasePath;
    }

    console.log("Initializing Seald SDK");

    sealdSDKInstance?.close();

    sealdDatabasePath = `seald-session-${sessionID}`;
    sealdSDKInstance = SealdSDK({
      appId: APP_ID,
      apiURL: API_URL,
      databaseKey,
      databasePath: sealdDatabasePath,
      plugins: [SealdSDKPluginSSKS2MR(STORAGE_URL)],
    });

    sealdSDKInstance.setLogLevel("info");

    try {
      await sealdSDKInstance.initialize();
    } catch (err) {
      handleSealdError(err);
    }

    // @ts-expect-error adding global variable for debug purposes
    window.sealdSDKInstance = sealdSDKInstance;
  };

  const handleSealdError = (err) => {
    console.error(err);
    Sentry.captureException(err, {
      tags: { category: "seald" },
      extra: {
        code: err.code,
        id: err.id,
        context: err.context,
      },
    });

    // If any of these errors occurs, we clean the local database and reload the tab
    if (
      ["CORRUPT_DATABASE", "LOGIN_WRONG_SIGNING_PUBKEY_HASH"].includes(err.code)
    ) {
      sealdSDKInstance.dropDatabase();
      window.location.reload();
    }

    throw err;
  };

  const createIdentity = async ({ signupJWT, databaseKey, sessionID }) => {
    await instantiateSealdSDK({ databaseKey, sessionID });

    try {
      await sealdSDKInstance.initiateIdentity({
        signupJWT: signupJWT,
      });
    } catch (err) {
      if (err.message != "Already registered") {
        throw err;
      }
    }

    return api.user.sendChallenge2MR(true);
  };

  const saveIdentity2MR = async ({
    userId,
    twoManRuleKey,
    emailAddress,
    twoManRuleSessionId,
    challenge,
    databaseKey,
    sessionID,
  }) => {
    await instantiateSealdSDK({ databaseKey, sessionID });

    const payload = {
      challenge,
      authFactor: {
        type: "EM" as const,
        value: emailAddress,
      },
      twoManRuleKey,
      userId,
      sessionId: twoManRuleSessionId,
    };

    await sealdSDKInstance.ssks2MR.saveIdentity(payload);
  };

  const retrieveIdentity2MR = async ({
    userId,
    emailAddress,
    twoManRuleKey,
    twoManRuleSessionId,
    challenge,
  }) => {
    return await sealdSDKInstance.ssks2MR.retrieveIdentity({
      challenge,
      authFactor: {
        type: "EM",
        value: emailAddress,
      },
      twoManRuleKey,
      userId,
      sessionId: twoManRuleSessionId,
    });
  };

  const retrieveIdentity = async ({ databaseKey, sessionID }) => {
    await instantiateSealdSDK({ databaseKey, sessionID });

    const status = await sealdSDKInstance.registrationStatus();

    if (status === "no-account") {
      const challengeData = await api.user.sendChallenge2MR();

      store.setChallengeData(challengeData);

      return false;
    }

    return true;
  };

  const createEncryptionSession = async ({
    userIds,
    name,
  }: {
    userIds: string[];
    name: string;
  }) => {
    if (!sealdSDKInstance) {
      throw new Error("Seald SDK not initialized");
    }

    return sealdSDKInstance.createEncryptionSession(
      {
        connectors: userIds.map(userIdToConnector),
      },
      { metadata: name }
    );
  };

  const retrieveEncryptionSession = async ({
    sessionId,
  }: {
    sessionId: string;
  }) => {
    if (!sealdSDKInstance) {
      throw new Error("Seald SDK not initialized");
    }

    return sealdSDKInstance.retrieveEncryptionSession({ sessionId });
  };

  const createGroup = async ({ groupName, members, admins }) => {
    if (!sealdSDKInstance) {
      throw new Error("Seald SDK not initialized");
    }

    return await sealdSDKInstance.createGroup({
      groupName,
      members,
      admins,
    });
  };

  const listGroups = async () => {
    if (!sealdSDKInstance) {
      throw new Error("Seald SDK not initialized");
    }

    return (
      await sealdSDKInstance
        .listGroups({ mine: true, all: true })
        .catch(handleSealdError)
    ).results;
  };

  return {
    get sealdSDKInstance() {
      return sealdSDKInstance;
    },
    createIdentity,
    retrieveIdentity2MR,
    retrieveIdentity,
    createEncryptionSession,
    retrieveEncryptionSession,
    userIdToConnector,
    saveIdentity2MR,
    createGroup,
    listGroups,
    store,
  };
};
